import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useLang } from "@/i18n";
import { getAttribution } from "@/lib/attribution";
import { pushEvent } from "@/lib/analytics";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import { CheckCircle2, ArrowRight, ArrowLeft, Plane, Mountain, Trophy, Users, Car } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = [Plane, Mountain, Trophy, Users, Car];

export default function QuizForm() {
  const { t } = useLang();
  const [step, setStep] = useState(1);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    trip_type: "", timeline: "", budget: "",
    name: "", email: "", phone: "", consent: false,
  });

  const progress = (step / 4) * 100;

  const next = () => {
    if (step === 1 && !form.trip_type) return toast.error(t.quiz.errSelectTrip);
    if (step === 2 && !form.timeline) return toast.error(t.quiz.errSelectTime);
    if (step === 3 && !form.budget) return toast.error(t.quiz.errSelectBudget);
    setStep((s) => Math.min(4, s + 1));
  };
  const back = () => setStep((s) => Math.max(1, s - 1));

  const submit = async () => {
    if (!form.name.trim()) return toast.error(t.quiz.errName);
    if (!form.email.trim()) return toast.error(t.quiz.errEmail);
    if (!form.consent) return toast.error(t.quiz.errConsent);
    setLoading(true);
    try {
      const attribution = getAttribution();
      await axios.post(`${API}/leads`, { ...form, ...attribution });
      pushEvent('generate_lead', { form_name: 'quiz', trip_type: form.trip_type, budget: form.budget, ...attribution });
      pushEvent('form_submit', { form_name: 'quiz' });
      setSubmitted(true);
      toast.success(t.quiz.sent);
    } catch {
      toast.error(t.quiz.failed);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div data-testid="quiz-success" className="bg-navy/95 backdrop-blur rounded-2xl p-8 sm:p-10 shadow-2xl text-center animate-fade-up border border-gold/30">
        <div className="mx-auto w-16 h-16 rounded-full bg-gold/15 border border-gold/40 flex items-center justify-center mb-5">
          <CheckCircle2 className="w-9 h-9 text-gold" />
        </div>
        <h3 className="font-display text-3xl text-cream mb-2">{t.quiz.successTitle}</h3>
        <p className="text-cream/75 max-w-md mx-auto">{t.quiz.successDesc}</p>
      </div>
    );
  }

  return (
    <div data-testid="quiz-form" className="bg-navy/90 backdrop-blur-md rounded-2xl shadow-2xl border border-navy-line overflow-hidden">
      <div className="px-6 sm:px-8 pt-6">
        <div className="flex items-center justify-end text-xs uppercase tracking-widest text-gold font-semibold mb-3">
          <span data-testid="quiz-step-label">{t.quiz.step} {step} {t.quiz.of} 4</span>
        </div>
        <Progress value={progress} className="h-1.5 bg-navy-deep" />
      </div>

      <div className="px-6 sm:px-8 py-7">
        {step === 1 && (
          <div className="animate-fade-up">
            <h3 className="font-display text-2xl sm:text-3xl text-cream mb-1">{t.quiz.q1Title}</h3>
            <p className="text-sm text-cream/60 mb-5">{t.quiz.q1Sub}</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {t.quiz.tripOptions.map((opt, i) => {
                const Icon = ICONS[i];
                const selected = form.trip_type === opt;
                return (
                  <button
                    key={opt}
                    data-testid={`quiz-trip-${i}`}
                    data-selected={selected}
                    onClick={() => setForm({ ...form, trip_type: opt })}
                    className="quiz-option rounded-xl px-4 py-4 text-left flex items-center gap-3"
                  >
                    <span className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${selected ? "bg-gold text-navy-deep" : "bg-navy-mid text-gold"}`}>
                      <Icon className="w-5 h-5" />
                    </span>
                    <span className="font-semibold text-sm text-cream leading-tight">{opt}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="animate-fade-up">
            <h3 className="font-display text-2xl sm:text-3xl text-cream mb-1">{t.quiz.q2Title}</h3>
            <p className="text-sm text-cream/60 mb-5">{t.quiz.q2Sub}</p>
            <div className="grid grid-cols-1 gap-2.5">
              {t.quiz.timelineOptions.map((opt, i) => (
                <button
                  key={opt}
                  data-testid={`quiz-timeline-${i}`}
                  data-selected={form.timeline === opt}
                  onClick={() => setForm({ ...form, timeline: opt })}
                  className="quiz-option rounded-xl px-4 py-3 text-left font-medium text-cream"
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="animate-fade-up">
            <h3 className="font-display text-2xl sm:text-3xl text-cream mb-1">{t.quiz.q3Title}</h3>
            <p className="text-sm text-cream/60 mb-5">{t.quiz.q3Sub}</p>
            <div className="grid grid-cols-1 gap-2.5">
              {t.quiz.budgetOptions.map((opt, i) => (
                <button
                  key={opt}
                  data-testid={`quiz-budget-${i}`}
                  data-selected={form.budget === opt}
                  onClick={() => setForm({ ...form, budget: opt })}
                  className="quiz-option rounded-xl px-4 py-3 text-left font-medium text-cream"
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="animate-fade-up space-y-3">
            <h3 className="font-display text-2xl sm:text-3xl text-cream mb-1">{t.quiz.q4Title}</h3>
            <p className="text-sm text-cream/60 mb-4">{t.quiz.q4Sub}</p>
            <div>
              <Label className="text-xs uppercase tracking-wider text-cream/80">{t.quiz.name} *</Label>
              <Input data-testid="quiz-input-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-navy-deep/60 border-navy-line text-cream placeholder:text-cream/40" placeholder="Jan Kowalski" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-cream/80">{t.quiz.email} *</Label>
                <Input data-testid="quiz-input-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1 bg-navy-deep/60 border-navy-line text-cream placeholder:text-cream/40" placeholder="jan@example.com" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-cream/80">{t.quiz.phone}</Label>
                <Input data-testid="quiz-input-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 bg-navy-deep/60 border-navy-line text-cream placeholder:text-cream/40" placeholder="+48 ..." />
              </div>
            </div>
            <label className="flex items-start gap-2 pt-1 cursor-pointer">
              <Checkbox data-testid="quiz-consent" checked={form.consent} onCheckedChange={(v) => setForm({ ...form, consent: !!v })} className="mt-0.5 border-gold/50 data-[state=checked]:bg-gold data-[state=checked]:text-navy-deep" />
              <span className="text-xs text-cream/70 leading-relaxed">{t.quiz.consent}</span>
            </label>
          </div>
        )}
      </div>

      <div className="px-6 sm:px-8 pb-6 pt-2 flex items-center justify-between gap-3 border-t border-navy-line bg-navy-deep/40">
        <Button data-testid="quiz-back-btn" type="button" variant="ghost" onClick={back} disabled={step === 1} className="text-cream hover:bg-navy-mid">
          <ArrowLeft className="w-4 h-4 mr-1" /> {t.quiz.back}
        </Button>
        {step < 4 ? (
          <Button data-testid="quiz-next-btn" type="button" onClick={next} className="btn-primary px-6">
            {t.quiz.next} <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        ) : (
          <Button data-testid="quiz-submit-btn" type="button" onClick={submit} disabled={loading} className="btn-primary px-6">
            {loading ? t.quiz.sending : t.quiz.submit}
          </Button>
        )}
      </div>

      <div className="px-6 sm:px-8 pb-5 text-[11px] text-cream/50 text-center">{t.quiz.footer}</div>
    </div>
  );
}
